import { useState, useEffect, createContext, useContext } from 'react';
import { useAuth } from '../components/AuthContext';

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const { adminEmail, carrierEmail, shipperEmail } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPermissions() {
      try {
        let userRole = 'BROKER_AGENT';
        let headers = {};
        
        if (adminEmail) {
          userRole = adminEmail.includes('accounting') ? 'ACCOUNTING' : 'BROKER_ADMIN';
          headers['x-admin-email'] = adminEmail;
        } else if (shipperEmail) {
          userRole = 'SHIPPER';
          headers['x-shipper-email'] = shipperEmail;
        } else if (carrierEmail) {
          userRole = 'CARRIER';
          headers['x-carrier-email'] = carrierEmail;
        }
        
        headers['x-user-role'] = userRole;
        
        const res = await fetch('/api/permissions/my-permissions', { headers });
        const data = await res.json();
        
        setRole(data.role || userRole);
        setPermissions(data.permissions || {});
      } catch (e) {
        console.error('Failed to load permissions:', e);
      } finally {
        setLoading(false);
      }
    }
    
    loadPermissions();
  }, [adminEmail, carrierEmail, shipperEmail]);

  const hasPermission = (permission) => {
    if (role === 'BROKER_ADMIN') return true;
    return !!permissions[permission];
  };

  const hasAnyPermission = (...perms) => {
    if (role === 'BROKER_ADMIN') return true;
    return perms.some(p => permissions[p]);
  };

  const hasAllPermissions = (...perms) => {
    if (role === 'BROKER_ADMIN') return true;
    return perms.every(p => permissions[p]);
  };

  return (
    <PermissionsContext.Provider value={{ role, permissions, loading, hasPermission, hasAnyPermission, hasAllPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    return {
      role: null,
      permissions: {},
      loading: true,
      hasPermission: () => false,
      hasAnyPermission: () => false,
      hasAllPermissions: () => false
    };
  }
  return context;
}

export default usePermissions;
